import { createContext, memo, useEffect, useMemo, useState } from "react";

import { Canvas } from "@react-three/fiber";

// import Timer from "./components/feature/timer/Timer";

import { GlobalStyle } from "./styles";
import Scene from "./components/feature/scene/Scene";

import React from "react";

import { useObjectData } from "./hooks/graphic/use-object-data";
import Timer from "./components/feature/timer/Timer";
import InfoList from "./components/feature/overlay/InfoList";
import {
  useInteractObjectStore,
  useSpaceObjectStore,
  useTimeControlStore
} from "./stores";
import dayjs from "dayjs";
import { KDIC_SAT, NTU_SAT, MALAY_SAT } from "@/constants";
import {
  malligyongIds,
  nuriIds,
  NTU_SAT_IDS,
  KDIC_IDS,
  MALAY_IDS
} from "./constants/sats";
import { CameraType } from "./types";
import Time2D from "./components/feature/timer/Time2D";
import useFullscreen from "./hooks/common/use-fullscreen";
import FullScreenButton from "./components/feature/overlay/FullScreenButton";

type AppProps = {
  map: string;
  bump: string;
  clouds: string;
  time?: string;
  noradId?: number;
  cameraType?: CameraType;
  satType: string;
  secondaryID: number;
  timeFormat?: string;
  defaultLabel?: string;
};

export const App = ({
  map,
  bump,
  clouds,
  time,
  noradId,
  cameraType = "PERSPECTIVE",
  satType,
  secondaryID,
  timeFormat = "KST",
  defaultLabel = "expand"
}: AppProps) => {
  createContext({});

  const getPrimaryIds = satType => {
    switch (satType) {
      case "Nuri":
        return nuriIds;
      case NTU_SAT:
        return NTU_SAT_IDS;
      case KDIC_SAT:
        return KDIC_IDS;
      case MALAY_SAT:
        return MALAY_IDS;
      default:
        return malligyongIds;
    }
  };

  const primaryIds = useMemo(() => {
    const ids = getPrimaryIds(satType);
    if (cameraType === "ORTHOGRAPHIC") {
      return [ids[0]];
    }
    return ids;
  }, [satType, cameraType]);
  const secondaryIds = useMemo(() => {
    if (secondaryID) {
      return [secondaryID];
    }
    return [];
  }, [secondaryID]);

  const { objectData: primary } = useObjectData(primaryIds);
  const { objectData: secondary } = useObjectData(secondaryIds);

  const [isFullScreen, setIsFullScreen] = useState(false);

  const objectData = useMemo(
    () => primary.concat(secondary),
    [primary, secondary]
  );

  const onFullScreen = isFull => {
    setIsFullScreen(isFull);
  };

  const { element, triggerFull, exitFull } = useFullscreen(onFullScreen);

  useEffect(() => {
    if (time) {
      const setDefaultTime = useTimeControlStore.getState().setDefaultTime;
      setDefaultTime(dayjs(time));
    }

    if (noradId) {
      const find = useSpaceObjectStore.getState().find;
      const setFocus = useInteractObjectStore.getState().setFocus;
      const object = find(Number(noradId));
      if (!object) return;

      setTimeout(() => setFocus(object));
    }
  }, [time, noradId]);

  return (
    <>
      <GlobalStyle />
      <div id="scene" ref={element} style={containerStyle}>
        <Canvas
          gl={{ antialias: true, logarithmicDepthBuffer: true }}
          frameloop="always"
        >
          <Scene
            objectData={objectData}
            status={"success"}
            map={map}
            bump={bump}
            clouds={clouds}
            cameraType={cameraType}
            defaultLabel={defaultLabel}
          />
        </Canvas>
        {cameraType === "PERSPECTIVE" ? (
          <>
            <Timer timeFormat={timeFormat} />

            <InfoList data={primary} satType={satType} />

            <FullScreenButton
              isFullScreen={isFullScreen}
              triggerFull={triggerFull}
              exitFull={exitFull}
            />
          </>
        ) : (
          <Time2D />
        )}
      </div>
    </>
  );
};

export default memo(App);

const containerStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  background: "black",
  position: "relative",
  overflow: "hidden"
};
